export const hasRole = (user, roles) => {
  if (typeof roles === 'string')
    roles = [roles];
  if (!user || !user.groups)
    return false;
  return roles.some(role => user.groups.includes(role));
}

// Permesso effettivo del responsabile sull'azienda corrente.
//
// `GET /user` allega a ogni azienda in cui l'utente è Responsabile un oggetto
// `supervisor_permissions` con i 13 flag risolti dal profilo assegnato, oppure
// — per chi non ha profilo — dai flag `allows_supervisor_*` dell'azienda.
// Da preferire sempre a quei flag: leggerli direttamente ignora i profili.
//
// Riproduce la semantica che le condizioni avevano prima dei profili:
//   hasRole('Azienda') || (hasRole('Responsabile') && allows_supervisor_X)
// cioè Azienda/Studio/Operatore senza limiti, Responsabile secondo i permessi,
// tutti gli altri ruoli (Responsabile Sicurezza, Timesheet, Dipendente) esclusi.
// Attenzione a non "semplificare" in un default true per i non-Responsabile:
// aprirebbe a quei ruoli tab e dati che oggi non vedono.
//
// Permessi mancanti su un Responsabile → false (fail closed): è ciò che faceva
// il flag azienda quando era undefined, ed è il default giusto per un gate.
// NB: è solo presentazione. Il confine di sicurezza resta il server.
export const hasSupervisorPermission = (user, permission) => {
  if (hasRole(user, ['Azienda', 'Studio', 'Operatore']))
    return true;

  if (!hasRole(user, 'Responsabile'))
    return false;

  const company_id = user?.data?.current_company;
  const permissions = user?.data?.companies?.[company_id]?.supervisor_permissions;

  return permissions?.[permission] === true;
}

// Gate per le voci aperte anche a ruoli estranei ai profili (Timesheet,
// ResponsabileSicurezza, …): limita solo il Responsabile, come il server
// (`isRestricted`: Responsabile senza Azienda/Studio/Operatore). Basta una chiave.
// Gli altri ruoli mantengono le condizioni già presenti accanto al gate.
export const supervisorProfileAllows = (user, permissions) => {
  if (!hasRole(user, 'Responsabile'))
    return true;
  const keys = typeof permissions === 'string' ? [permissions] : permissions;
  return keys.some((key) => hasSupervisorPermission(user, key));
}

// Ambito di visibilità del responsabile: 'SUBTREE' (gerarchia supervisor_id),
// 'AREA' (dipendenti delle aree gestite) oppure 'COMPANY' (tutta l'azienda).
// Serve ai filtri team/area.
export const supervisorScopeType = (user) => {
  const company_id = user?.data?.current_company;
  return user?.data?.companies?.[company_id]?.supervisor_scope_type ?? 'SUBTREE';
}

// Ambito delle note spese: può essere 'COMPANY' anche per un responsabile di team o area.
export const supervisorExpensesScopeType = (user) => {
  const company = user?.data?.companies?.[user?.data?.current_company];
  return company?.supervisor_expenses_scope_type ?? company?.supervisor_scope_type ?? 'SUBTREE';
}

// Responsabile che vede solo le note spese: la dashboard lo rimanda alle note spese,
// quindi anche la voce di menu Dashboard va nascosta.
export const isExpensesOnlySupervisor = (user) => {
  const company = user?.data?.companies?.[user?.data?.current_company];
  return hasRole(user, 'Responsabile')
    && !hasRole(user, ['Timesheet', 'ResponsabileSicurezza'])
    && !!company?.feature_expense_reports
    && hasSupervisorPermission(user, 'expenses_view')
    && !['timesheet_view', 'requests_view', 'report_access'].some((key) => hasSupervisorPermission(user, key));
}
